import { IconOption } from 'rwl-shared/dist/models/widget';
import { faHouse, faLocationDot } from '@fortawesome/pro-solid-svg-icons';
import { NeighborhoodServiceAreaInfo } from 'rwl-shared/dist/models';

export const adjustMapViewport = (
    map: google.maps.Map,
    boundsArray: google.maps.LatLngBoundsLiteral[]
) => {
    const viewport = new google.maps.LatLngBounds();
    boundsArray.forEach((bounds) => {
        viewport.union(bounds);
    });
    map.fitBounds(viewport);
};

export const getIcon = (icon: IconOption) => {
    switch (icon) {
        case IconOption.PointerDark:
        case IconOption.PointerLight:
            return faLocationDot;
        case IconOption.House:
        default:
            return faHouse;
    }
};

export const getIconColor = (icon: IconOption, mapIconColor: string) => {
    switch (icon) {
        case IconOption.PointerDark:
            return { fill: 'rgba(0, 0, 0, 0.87)', backDrop: mapIconColor };
        case IconOption.PointerLight:
            return { fill: 'white', backDrop: mapIconColor };
        case IconOption.House:
        default:
            return { fill: mapIconColor, backDrop: 'white' };
    }
};

/** Simple Haversine formula to compute distance in meters. */
export function getDistanceMeters(
    loc1: google.maps.LatLngLiteral,
    loc2: google.maps.LatLngLiteral
) {
    const R = 6371e3; // Earth radius (m)
    const rad = Math.PI / 180;
    const dLat = (loc2.lat - loc1.lat) * rad;
    const dLng = (loc2.lng - loc1.lng) * rad;
    const a =
        Math.sin(dLat / 2) ** 2 +
        Math.cos(loc1.lat * rad) *
            Math.cos(loc2.lat * rad) *
            Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export const BASE_ZOOM = 14;

/** Zoom-based distance threshold (in meters). Tweak to your needs. */
export function getDistanceThresholdForZoom(zoom: number) {
    const baseThreshold = 300; // e.g. 300m radius at zoom=14
    const referenceZoom = BASE_ZOOM;
    const factor = 2 ** (referenceZoom - zoom);
    return baseThreshold * factor;
}

/** Compute lat/lng centroid for a group of service areas. */
export function computeCentroid(
    group: NeighborhoodServiceAreaInfo[]
): google.maps.LatLngLiteral {
    let sumLat = 0;
    let sumLng = 0;
    let count = 0;
    for (const area of group) {
        const loc = area.placeInfo?.geometry?.location;
        if (loc) {
            sumLat += loc.lat;
            sumLng += loc.lng;
            count++;
        }
    }
    return count
        ? { lat: sumLat / count, lng: sumLng / count }
        : { lat: 0, lng: 0 };
}

export interface Cluster {
    centroid: google.maps.LatLngLiteral;
    serviceAreas: NeighborhoodServiceAreaInfo[];
    totalJobs: number;
}

export function clusterServiceAreas(
    serviceAreas: NeighborhoodServiceAreaInfo[],
    zoom: number
): Cluster[] {
    const threshold = getDistanceThresholdForZoom(zoom);
    const clusters: Cluster[] = [];

    for (const area of serviceAreas) {
        const loc = area.placeInfo?.geometry?.location;
        if (!loc) continue;

        let assigned = false;
        for (const cluster of clusters) {
            const dist = getDistanceMeters(cluster.centroid, loc);
            if (dist < threshold) {
                cluster.serviceAreas.push(area);
                cluster.totalJobs += area.jobCount;
                // Recalculate centroid
                cluster.centroid = computeCentroid(cluster.serviceAreas);
                assigned = true;
                break;
            }
        }

        if (!assigned) {
            clusters.push({
                centroid: loc,
                serviceAreas: [area],
                totalJobs: area.jobCount,
            });
        }
    }

    return clusters;
}
